// ════════════════════════════════════════════════════════════════════════════
// ConflitoModal — r147
// Modal de aviso quando o backend retorna 409 (conflito de horários).
// Mostra a lista dos blocos conflitantes + opção de forçar criação.
// ════════════════════════════════════════════════════════════════════════════

import React from 'react';

const COR = {
  azul:    '#003399',
  azulBg:  '#eff6ff',
  borda:   '#e8eaed',
  texto:   '#1a1a2e',
  muted:   '#888',
  branco:  '#fff',
  verde:   '#1D9E75',
  amarelo: '#d97706',
  amlBg:   '#fffbeb',
  vermelho:'#e53935',
  cinzaBg: '#fafbfc',
};

function fmtFaixa(ini, fim) {
  const di = new Date(ini);
  const df = new Date(fim);
  const dia = di.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
  const hi = di.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  const hf = df.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  return `${dia}  •  ${hi} - ${hf}`;
}

const TIPO_LABEL = {
  TAREFA: { icone: '⏱', cor: COR.verde,   texto: 'Apontamento' },
  TAREFA_PLANEJADA: { icone: '📅', cor: COR.azul, texto: 'Planejado' },
  PROJETO: { icone: '🗂', cor: '#7c3aed', texto: 'Projeto'  },
  FERIAS:  { icone: '🏖', cor: '#06b6d4', texto: 'Férias'   },
  SUPORTE: { icone: '🆘', cor: '#f97316', texto: 'Suporte'  },
  OUTLOOK: { icone: '📧', cor: '#0078d4', texto: 'Outlook'  },
};

function ConflitoModal({ aberto, conflitos, mensagem, onConfirmar, onCancelar, processando }) {
  if (!aberto) return null;

  return (
    <div
      onClick={onCancelar}
      style={{
        position: 'fixed', inset: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        zIndex: 1200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          backgroundColor: COR.branco,
          borderRadius: '8px',
          width: '520px', maxWidth: '92vw',
          overflow: 'hidden',
          fontFamily: "'Manrope', sans-serif",
          boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
        }}
      >
        {/* Header */}
        <div style={{
          backgroundColor: COR.amarelo, color: COR.branco,
          padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span style={{ fontSize: '18px' }}>⚠</span>
          <span style={{ fontSize: '15px', fontWeight: '700', flex: 1 }}>Conflito de Horários</span>
        </div>

        {/* Corpo */}
        <div style={{ padding: '16px' }}>
          <div style={{ fontSize: '13px', color: COR.texto, marginBottom: '14px', lineHeight: 1.5 }}>
            {mensagem || 'Já existem blocos no calendário no horário escolhido.'}
          </div>

          {/* Lista de conflitos */}
          {Array.isArray(conflitos) && conflitos.length > 0 && (
            <div style={{
              border: `1px solid ${COR.borda}`,
              borderRadius: '5px', overflow: 'hidden',
              marginBottom: '14px', maxHeight: '240px', overflowY: 'auto',
            }}>
              {conflitos.map((c, i) => {
                const t = TIPO_LABEL[c.tipo] || { icone: '📌', cor: COR.muted, texto: c.tipo };
                return (
                  <div key={c.id || i} style={{
                    padding: '10px 12px',
                    borderBottom: i < conflitos.length - 1 ? `1px solid ${COR.borda}` : 'none',
                    backgroundColor: i % 2 === 0 ? COR.branco : COR.cinzaBg,
                    display: 'flex', alignItems: 'center', gap: '10px',
                  }}>
                    <span style={{
                      fontSize: '10px', fontWeight: '700', color: t.cor,
                      padding: '2px 8px', border: `1px solid ${t.cor}`,
                      borderRadius: '12px', whiteSpace: 'nowrap',
                      display: 'flex', alignItems: 'center', gap: '3px',
                    }}>
                      <span>{t.icone}</span> {t.texto}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', color: COR.texto, fontWeight: '500' }}>
                        {fmtFaixa(c.data_inicio, c.data_fim)}
                      </div>
                      {c.observacao && (
                        <div style={{
                          fontSize: '11px', color: COR.muted,
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        }}>
                          {c.observacao}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{
            padding: '8px 12px', borderRadius: '4px',
            backgroundColor: COR.amlBg, border: `1px solid ${COR.amarelo}`,
            fontSize: '11px', color: COR.amarelo,
          }}>
            Se confirmar, o bloco vai ser criado mesmo com sobreposição. Os blocos existentes
            não são apagados — vai ficar tudo no mesmo horário.
          </div>
        </div>

        {/* Rodapé */}
        <div style={{
          padding: '12px 16px', borderTop: `1px solid ${COR.borda}`,
          backgroundColor: COR.cinzaBg,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          <button onClick={onCancelar} disabled={processando} style={btnSec}>
            Cancelar
          </button>
          <button onClick={onConfirmar} disabled={processando} style={btnPri}>
            {processando ? 'Salvando...' : 'Salvar mesmo assim'}
          </button>
        </div>
      </div>
    </div>
  );
}

const btnBase = {
  padding: '8px 16px', borderRadius: '5px',
  fontSize: '12px', fontWeight: '600', cursor: 'pointer',
  fontFamily: "'Manrope', sans-serif",
};
const btnSec = {
  ...btnBase,
  border: `1px solid ${COR.borda}`,
  backgroundColor: COR.branco, color: COR.texto,
};
const btnPri = {
  ...btnBase,
  border: `1px solid ${COR.amarelo}`,
  backgroundColor: COR.amarelo, color: COR.branco,
};

export default ConflitoModal;
